import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Camera, X, Loader2, ImagePlus, MapPinOff } from "lucide-react";
import { toast } from "sonner";
import { requestLocation } from "@/lib/geo";
import { LocationHelp } from "@/components/LocationHelp";
import { Lightbox } from "@/components/Lightbox";

function getPosition() {
  return requestLocation().then((r) => (r.ok ? r.pos : null));
}

function drawStamp(ctx, w, h, pos, capturedAt) {
  const pad = Math.round(w * 0.02);
  const fs = Math.max(14, Math.round(w * 0.022));
  const line1 = `RetroTrack  •  ${new Date(capturedAt).toLocaleString("en-GB")}`;
  const line2 = pos
    ? `GPS ${pos.lat.toFixed(6)}, ${pos.lng.toFixed(6)}  (±${Math.round(pos.accuracy)}m)`
    : "GPS location unavailable";
  const barH = fs * 2 + pad * 2.2;
  ctx.fillStyle = "rgba(15,23,42,0.72)";
  ctx.fillRect(0, h - barH, w, barH);
  ctx.fillStyle = "#4ADE80";
  ctx.fillRect(0, h - barH, Math.round(w * 0.012), barH);
  ctx.font = `bold ${fs}px sans-serif`;
  ctx.fillStyle = "#ffffff";
  ctx.textBaseline = "top";
  ctx.fillText(line1, pad * 1.5, h - barH + pad * 0.7);
  ctx.font = `${fs}px monospace`;
  ctx.fillStyle = "#E2E8F0";
  ctx.fillText(line2, pad * 1.5, h - barH + pad * 0.9 + fs);
}

function fileToCanvas(file, pos, capturedAt) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 1600 / (img.naturalWidth || img.width));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round((img.naturalWidth || img.width) * scale);
      canvas.height = Math.round((img.naturalHeight || img.height) * scale);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      drawStamp(ctx, canvas.width, canvas.height, pos, capturedAt);
      URL.revokeObjectURL(img.src);
      resolve(canvas);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

const toPhoto = (i) => ({ id: i.id, lat: i.lat ?? null, lng: i.lng ?? null, accuracy: i.accuracy ?? null, captured_at: i.captured_at, caption: i.caption || "" });

// Full-screen live camera — snap consecutively (uploads run in the background), thumbnails build along the bottom
export function CameraCapture({ open, onClose, photos = [], onChange, single = false, streamPromise = null, locationPromise = null }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const galleryRef = useRef(null);
  const itemsRef = useRef([]);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [items, setItems] = useState([]);
  const [ready, setReady] = useState(false);
  const [pos, setPos] = useState(null);
  const posRef = useRef(null);
  const [err, setErr] = useState(null);
  const [geoCode, setGeoCode] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const setPosition = (p) => { posRef.current = p; setPos(p); };

  const retryLocation = async () => {
    setRetrying(true);
    const r = await requestLocation();
    setRetrying(false);
    if (r.ok) {
      setPosition(r.pos);
      setGeoCode(null);
      setShowHelp(false);
      // backfill any already-captured shots that have no coordinates
      commit(itemsRef.current.map((i) => (i.lat == null ? { ...i, lat: r.pos.lat, lng: r.pos.lng, accuracy: r.pos.accuracy } : i)));
      toast.success("Location captured");
    } else {
      setGeoCode(r.code);
    }
  };

  const commit = (next) => {
    itemsRef.current = next;
    setItems(next);
    onChangeRef.current(next.filter((i) => i.id).map(toPhoto));
  };

  useEffect(() => {
    if (!open) return;
    const seed = photos.map((p) => ({ key: p.id, id: p.id, url: fileUrl(p.id, 200), uploading: false, ...p }));
    itemsRef.current = seed;
    setItems(seed);
    setErr(null);
    setReady(false);
    setGeoCode(null);
    setShowHelp(false);
    let active = true;
    let watchId = null;
    posRef.current = null;
    setPos(null);
    // Use the in-gesture location request from the tap (reliable iOS prompt); else fetch now
    (locationPromise || requestLocation()).then((r) => {
      if (!active) return;
      if (r && r.ok) setPosition(r.pos);
      else setGeoCode(r ? r.code : 2);
    });
    if (navigator.geolocation) {
      watchId = navigator.geolocation.watchPosition(
        (p) => { if (active) { setPosition({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }); setGeoCode(null); } },
        () => {},
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 8000 }
      );
    }
    (async () => {
      if (!streamPromise && !navigator.mediaDevices?.getUserMedia) {
        setErr("This browser can't open the live camera (in-app browsers often block it). Open the site in Safari or Chrome, or tap 'Choose from gallery' to add several photos at once.");
        return;
      }
      let stream;
      try {
        if (streamPromise) {
          stream = await streamPromise;
        } else {
          stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false })
            .catch(() => navigator.mediaDevices.getUserMedia({ video: true, audio: false }));
        }
      } catch (e2) {
        const name = e2?.name || "";
        if (name === "NotAllowedError" || name === "SecurityError")
          setErr("Camera access is blocked. Allow camera permission for this site (Settings ▸ Safari ▸ Camera, or the 'aA' menu in the address bar), then reopen — or tap 'Choose from gallery'.");
        else if (name === "NotFoundError" || name === "OverconstrainedError")
          setErr("No camera was found on this device. Tap 'Choose from gallery' to add photos instead.");
        else
          setErr("Couldn't start the live camera on this device. Tap 'Choose from gallery' to add several photos at once.");
        return;
      }
      if (!active) { stream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
      setReady(true);
    })();
    return () => {
      active = false;
      if (watchId != null && navigator.geolocation) navigator.geolocation.clearWatch(watchId);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const uploadCanvas = async (canvas, key) => {
    try {
      const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.85));
      const fd = new FormData();
      fd.append("file", blob, `photo-${Date.now()}.jpg`);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      commit(itemsRef.current.map((i) => (i.key === key ? { ...i, id: data.id, uploading: false } : i)));
    } catch (e) {
      toast.error("A photo failed to upload");
      commit(itemsRef.current.filter((i) => i.key !== key));
    }
  };

  const snap = async () => {
    const v = videoRef.current;
    if (!v || !ready) return;
    const vw = v.videoWidth || 1280;
    const vh = v.videoHeight || 960;
    const scale = Math.min(1, 1600 / vw);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(vw * scale);
    canvas.height = Math.round(vh * scale);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    const capturedAt = new Date().toISOString();
    const shotPos = posRef.current;
    drawStamp(ctx, canvas.width, canvas.height, shotPos, capturedAt);
    const key = `t-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    commit([...itemsRef.current, { key, url: canvas.toDataURL("image/jpeg", 0.5), uploading: true, lat: shotPos?.lat ?? null, lng: shotPos?.lng ?? null, accuracy: shotPos?.accuracy ?? null, captured_at: capturedAt }]);
    uploadCanvas(canvas, key);
    // No live fix yet: fetch one now and backfill the photo's coordinates (map picks it up)
    if (!shotPos) {
      requestLocation().then((r) => {
        if (r.ok) {
          setPosition(r.pos);
          setGeoCode(null);
          commit(itemsRef.current.map((i) => (i.key === key ? { ...i, lat: r.pos.lat, lng: r.pos.lng, accuracy: r.pos.accuracy } : i)));
        } else {
          setGeoCode(r.code);
          toast.warning("Photo saved without GPS — tap the location banner to enable it.");
        }
      });
    }
    if (single) setTimeout(() => onClose(), shotPos ? 500 : 2400);
  };

  const handleGallery = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const cpos = posRef.current || pos || (await getPosition());
    const base = Date.now();
    // Prepare every file first (stamp), then upload them all in parallel so nothing is dropped
    const prepared = [];
    for (let idx = 0; idx < files.length; idx++) {
      const capturedAt = new Date().toISOString();
      const key = `g-${base}-${idx}`;
      try {
        // eslint-disable-next-line no-await-in-loop
        const canvas = await fileToCanvas(files[idx], cpos, capturedAt);
        prepared.push({ key, canvas, capturedAt });
      } catch (e) {
        toast.error("A selected image couldn't be read");
      }
    }
    if (!prepared.length) return;
    commit([
      ...itemsRef.current,
      ...prepared.map((p) => ({ key: p.key, url: p.canvas.toDataURL("image/jpeg", 0.4), uploading: true, lat: cpos?.lat, lng: cpos?.lng, accuracy: cpos?.accuracy, captured_at: p.capturedAt })),
    ]);
    const batch = single ? prepared.slice(0, 1) : prepared;
    await Promise.all(batch.map((p) => uploadCanvas(p.canvas, p.key)));
    if (galleryRef.current) galleryRef.current.value = "";
    if (single) onClose();
  };

  const remove = (key) => commit(itemsRef.current.filter((i) => i.key !== key));

  if (!open) return null;
  const uploadingCount = items.filter((i) => i.uploading).length;
  return (
    <div className="fixed inset-0 z-[120] flex flex-col bg-black" data-testid="camera-capture">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <span className="text-sm font-semibold" data-testid="camera-count">
          {items.length} photo{items.length === 1 ? "" : "s"}{uploadingCount ? ` · ${uploadingCount} saving…` : ""}
        </span>
        <button type="button" onClick={onClose} data-testid="camera-done" className="rounded-full bg-white/15 px-5 py-1.5 text-sm font-bold text-white transition-colors hover:bg-white/25">Done</button>
      </div>

      <div className="relative flex-1 overflow-hidden">
        {err ? (
          <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center text-sm text-white/80" data-testid="camera-error">
            <Camera className="h-10 w-10 text-white/40" />
            {err}
            <button type="button" onClick={() => galleryRef.current?.click()} className="rounded-full bg-accent px-5 py-2 text-sm font-bold text-white" data-testid="camera-gallery-fallback">
              Choose from gallery
            </button>
          </div>
        ) : (
          <video ref={videoRef} playsInline muted className="h-full w-full object-cover" />
        )}
        {!err && pos && (
          <div className="absolute left-3 top-3 rounded bg-black/60 px-2 py-1 text-[11px] font-medium text-white">GPS {pos.lat.toFixed(5)}, {pos.lng.toFixed(5)}</div>
        )}
        {!err && !pos && geoCode != null && (
          <button
            type="button"
            onClick={() => setShowHelp(true)}
            className="absolute left-3 top-3 flex items-center gap-1.5 rounded bg-red-600/90 px-2.5 py-1.5 text-[11px] font-bold text-white shadow"
            data-testid="camera-gps-banner"
          >
            <MapPinOff className="h-3.5 w-3.5" /> GPS unavailable — tap for help
          </button>
        )}
        {showHelp && (
          <LocationHelp code={geoCode} retrying={retrying} onRetry={retryLocation} onClose={() => setShowHelp(false)} />
        )}
      </div>

      {items.length > 0 && (
        <div className="flex gap-2 overflow-x-auto bg-black/85 p-3" data-testid="camera-thumbs">
          {items.map((p) => (
            <div key={p.key} className="relative shrink-0">
              <img
                src={p.url}
                alt="thumb"
                onClick={() => setLightbox(p.id ? fileUrl(p.id) : p.url)}
                className="h-16 w-16 cursor-pointer rounded-md object-cover"
                data-testid={`camera-thumb-${p.key}`}
              />
              {p.uploading && (
                <div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/40">
                  <Loader2 className="h-5 w-5 animate-spin text-white" />
                </div>
              )}
              <button type="button" onClick={() => remove(p.key)} data-testid={`camera-remove-${p.key}`} className="absolute -right-1.5 -top-1.5 rounded-full bg-red-600 p-0.5 text-white shadow">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {!err && (
        <div className="flex items-center justify-center gap-6 bg-black py-5">
          <button type="button" onClick={() => galleryRef.current?.click()} className="flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white" data-testid="camera-gallery">
            <ImagePlus className="h-5 w-5" />
          </button>
          <button type="button" onClick={snap} disabled={!ready} data-testid="camera-shutter" className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-white bg-white/20 transition-transform active:scale-90 disabled:opacity-40">
            <Camera className="h-7 w-7 text-white" />
          </button>
          <div className="h-11 w-11" />
        </div>
      )}

      <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleGallery(e.target.files)} />
      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
